import { supabase } from "@/services/supabase/client";
import { Database } from "@/services/supabase/types";
import { FetchedTag } from "./tags";
import { loadKeyFromDB } from "@/features/encryption/utils/storage";
import { processNoteContent, prepareEncryptedContent } from "../utils/note-encryption";
import { processTagName } from "../utils/tag-encryption";
import { TagColor } from "@/features/tags/types";

type DbNotes = Database["public"]["Tables"]["notes"];
export type NoteRow = DbNotes["Row"];
export type NoteInsert = DbNotes["Insert"];
export type NoteUpdate = DbNotes["Update"];

export interface FetchedNote
  extends Pick<
    NoteRow,
    | "encrypted_data"
    | "iv"
    | "id"
    | "html_content"
    | "text_content"
    | "public_uuid"
    | "pinned"
    | "locked"
    | "masked"
    | "folder_id"
    | "soft_deleted_at"
  > {
  tempId?: number;
  tags: FetchedTag[];
}

export type AddNote = Pick<NoteRow, "html_content" | "text_content" | "user_id" | "folder_id">;
export interface AddNoteRes
  extends Pick<
    NoteRow,
    | "encrypted_data"
    | "iv"
    | "id"
    | "html_content"
    | "text_content"
    | "public_uuid"
    | "pinned"
    | "locked"
    | "masked"
    | "folder_id"
    | "soft_deleted_at"
  > {
  tempId?: number;
}
export interface UpdateNoteRes
  extends Partial<
    Pick<
      NoteRow,
      | "encrypted_data"
      | "id"
      | "iv"
      | "html_content"
      | "text_content"
      | "public_uuid"
      | "pinned"
      | "locked"
      | "masked"
      | "folder_id"
      | "soft_deleted_at"
    >
  > {
  tempId?: number;
}

async function fetchNotes(lastSync: string | null = null): Promise<FetchedNote[]> {
  let query = supabase
    .from("notes")
    .select(
      `
      encrypted_data,
      iv,
      id, 
      html_content, 
      text_content,
      public_uuid,
      pinned,
      locked,
      masked,
      folder_id,
      soft_deleted_at,
      note_tags(
        tags(
          id,
          color,
          name,
          encrypted_data,
          iv
        )
      )
    `
    )
    .is("hard_deleted_at", null);

  // If lastSync is provided, only fetch notes updated since last sync
  if (lastSync) {
    query = query.gt("updated_at", lastSync);
  }

  const { data, error } = await query.order("id", { ascending: false });

  if (error) throw error;

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (note) => {
      const { text_content, html_content } = await processNoteContent(note, dek);

      const decryptedTags = await Promise.all(
        (note.note_tags || []).map(async (nt) => {
          const name = await processTagName(nt.tags, dek);
          return { ...nt.tags, name, color: nt.tags.color as TagColor | null };
        })
      );

      return {
        ...note,
        text_content,
        html_content,
        tags: decryptedTags,
      };
    })
  );
}

async function fetchNoteById(id: number): Promise<FetchedNote | null> {
  const { data, error } = await supabase
    .from("notes")
    .select(
      `
      encrypted_data,
      iv,
      id,
      html_content,
      text_content,
      public_uuid,
      pinned,
      locked,
      masked,
      folder_id,
      soft_deleted_at,
      note_tags(
        tags(
          id,
          color,
          name,
          encrypted_data,
          iv
        )
      )
    `
    )
    .eq("id", id)
    .single();

  if (error) throw error;

  const dek = await loadKeyFromDB();
  const { text_content, html_content } = await processNoteContent(data, dek);

  const decryptedTags = await Promise.all(
    (data.note_tags || []).map(async (nt) => {
      const name = await processTagName(nt.tags, dek);
      return { ...nt.tags, name, color: nt.tags.color as TagColor | null };
    })
  );

  return {
    ...data,
    text_content,
    html_content,
    tags: decryptedTags,
  };
}

async function getDeletedNotes(): Promise<FetchedNote[]> {
  const { data, error } = await supabase
    .from("notes")
    .select(
      `
      encrypted_data,
      iv,
      id, 
      html_content, 
      text_content,
      public_uuid,
      pinned,
      locked,
      masked,
      folder_id,
      soft_deleted_at,
      note_tags(
        tags(
          id,
          color,
          name,
          encrypted_data,
          iv
        ) 
      )
    `
    )
    .not("soft_deleted_at", "is", null)
    .is("hard_deleted_at", null)
    .order("id", { ascending: false });

  if (error) throw error;

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (note) => {
      const { text_content, html_content } = await processNoteContent(note, dek);

      const decryptedTags = await Promise.all(
        (note.note_tags || []).map(async (nt) => {
          const name = await processTagName(nt.tags, dek);
          return { ...nt.tags, name, color: nt.tags.color as TagColor | null };
        })
      );

      return {
        ...note,
        text_content,
        html_content,
        tags: decryptedTags,
      };
    })
  );
}

// TODO, user id should be added in the db
async function addNote(note: AddNote): Promise<AddNoteRes> {
  const dek = await loadKeyFromDB();
  const { encrypted_data, iv, clearContent } = await prepareEncryptedContent(note.text_content, note.html_content, dek);

  const insertData = {
    ...note,
    ...(clearContent && encrypted_data && iv
      ? { encrypted_data, iv, text_content: "", html_content: "" }
      : { encrypted_data: "", iv: "" }),
  };

  const { data, error } = await supabase
    .from("notes")
    .insert([insertData])
    .select(
      "encrypted_data, iv, id, html_content, text_content, public_uuid, pinned, locked, masked, folder_id, soft_deleted_at"
    )
    .single();

  if (error) throw error;

  return { ...data, text_content: note.text_content, html_content: note.html_content };
}

async function updateNote({
  id,
  note,
  shared,
}: {
  id: number;
  note: Partial<NoteUpdate>;
  shared: boolean;
}): Promise<UpdateNoteRes | null> {
  const dek = await loadKeyFromDB();

  // Prepare encryption if content is being updated
  if (note.text_content || note.html_content) {
    const { encrypted_data, iv, clearContent } = await prepareEncryptedContent(
      note.text_content,
      note.html_content,
      dek,
      shared
    );

    if (clearContent && encrypted_data && iv) {
      note.encrypted_data = encrypted_data;
      note.iv = iv;
      note.text_content = "";
      note.html_content = "";
    } else if (shared) {
      // If note is shared, ensure content is not encrypted
      note.encrypted_data = "";
      note.iv = "";
    }
  }

  const { data, error } = await supabase
    .from("notes")
    .update(note)
    .eq("id", id)
    .select("encrypted_data, iv, id, public_uuid, pinned, locked, masked, folder_id, soft_deleted_at")
    .maybeSingle();

  if (error) throw error;

  return data;
}

async function tempDeleteNote(id: number) {
  const { data, error } = await supabase
    .from("notes")
    .update({ soft_deleted_at: new Date().toISOString(), folder_id: null })
    .eq("id", id);
  if (error) throw error;
  return data;
}

async function hardDeleteNote(id: number) {
  const { data, error } = await supabase
    .from("notes")
    .update({ hard_deleted_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
  return data;
}

async function restoreNote(id: number): Promise<FetchedNote> {
  const { data, error } = await supabase
    .from("notes")
    .update({ soft_deleted_at: null })
    .eq("id", id)
    .select(
      `
      encrypted_data,
      iv,
      id, 
      html_content, 
      text_content,
      public_uuid,
      pinned,
      locked,
      masked,
      folder_id,
      soft_deleted_at,
      note_tags(
        tags(
          id,
          color,
          name,
          encrypted_data,
          iv
        )
      )
    `
    )
    .single();

  if (error) throw error;

  const dek = await loadKeyFromDB();

  const decryptedTags = await Promise.all(
    (data.note_tags || []).map(async (nt) => {
      const name = await processTagName(nt.tags, dek);
      return { ...nt.tags, name, color: nt.tags.color as TagColor | null };
    })
  );

  return { ...data, tags: decryptedTags };
}

export type FetchedSharedNote = Pick<NoteRow, "id" | "html_content" | "text_content">;

async function getNoteByPublicUuid(publicUuid: string): Promise<FetchedSharedNote> {
  const { data, error } = await supabase.rpc("get_shared_note", { p_public_uuid: publicUuid });

  if (error) throw error;
  return data[0];
}

async function fetchHardDeletedNotes(lastSync: string | null): Promise<number[]> {
  if (!lastSync) return [];

  const { data, error } = await supabase
    .from("notes")
    .select("id")
    .not("hard_deleted_at", "is", null)
    .gt("updated_at", lastSync);

  if (error) throw error;
  return data.map((n) => n.id);
}

export const notesFetchers = {
  fetchNotes,
  fetchNoteById,
  getDeletedNotes,
  addNote,
  updateNote,
  tempDeleteNote,
  hardDeleteNote,
  restoreNote,
  getNoteByPublicUuid,
  fetchHardDeletedNotes,
};
